import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
// Components
import JunoPage from "../../components/JunoPage/JunoPage";
// Resources
import { Organization } from "fhir/r5";
import { HealthcareService, Location } from "fhir/r5";
// FHIR Front Library
import { NarrativeDisplay } from "@fyrstain/fhir-front-library";
// HL7 Front Library
import { Title } from "@fyrstain/hl7-front-library";
// React BootStrap
import { Badge, Button, Card } from "react-bootstrap";
// Translation
import i18n from "i18next";
// Services
import HealthcareServiceService from "../../services/HealthcareServiceService";
import OrganizationService from "../../services/OrganizationService";
import LocationService from "../../services/LocationService";
import {
  getOrganizationTypeLabel,
  loadOrganizationTypeOptions,
  OrganizationTypeOption,
} from "../../services/OrganizationTypeService";
// Utilities
import { getOrganizationPath, getOrganizationKey } from "../../utils/organizationTree";
import {
  getLocationDisplayName,
  getLocationKey,
} from "../../utils/locationTree";
import {
  referenceMatchesValue,
} from "../../utils/fhirReferences";
// Styles
import "./OrganizationDetails.css";

function findOrganizationByIdentifier(
  organizations: Organization[],
  identifier: string
): Organization | null {
  const normalizedIdentifier = identifier.trim();

  if (!normalizedIdentifier) {
    return null;
  }

  return (
    organizations.find((candidate) => {
      const candidateIdentifiers =
        candidate.identifier
          ?.map((entry) => entry.value?.trim())
          .filter((value): value is string => Boolean(value)) ?? [];

      return (
        candidate.id?.trim() === normalizedIdentifier ||
        candidate.name?.trim() === normalizedIdentifier ||
        candidateIdentifiers.includes(normalizedIdentifier)
      );
    }) ?? null
  );
}

const OrganizationDetails: FunctionComponent = () => {
  //////////////////////////////
  //        Navigation        //
  //////////////////////////////

  const navigate = useNavigate();

  //////////////////////////////
  //      Data loading        //
  //////////////////////////////

  const [loading, setLoading] = useState(false);
  const [fitFooter, setFitFooter] = useState(false);
  const { organizationId } = useParams();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [allHealthcareServices, setAllHealthcareServices] = useState<HealthcareService[]>([]);
  const [organizationTypeOptions, setOrganizationTypeOptions] = useState<
    OrganizationTypeOption[]
  >([]);

  const onError = useCallback(() => {
    navigate("/Error");
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [organizationsResponse, locationsResponse, healthcareServicesResponse] =
        await Promise.all([
          OrganizationService.loadAllOrganizations(),
          LocationService.loadAllLocations(),
          HealthcareServiceService.loadAllHealthcareServices(),
        ]);

      let response: Organization | null = null;

      try {
        response = await OrganizationService.loadOrganization(
          organizationId as string
        );
      } catch (error) {
        response = findOrganizationByIdentifier(
          organizationsResponse,
          organizationId as string
        );
      }

      if (!response) {
        onError();
        return;
      }

      setFitFooter(!response?.text?.div);
      setOrganization(response);
      setAllOrganizations(organizationsResponse);
      setAllLocations(locationsResponse);
      setAllHealthcareServices(healthcareServicesResponse);
    } catch (error) {
      onError();
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [organizationId, onError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let active = true;

    loadOrganizationTypeOptions()
      .then((options) => {
        if (active) {
          setOrganizationTypeOptions(options);
        }
      })
      .catch(() => {
        if (active) {
          setOrganizationTypeOptions([]);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  //////////////////////////////
  //     Derived data         //
  //////////////////////////////

  const primaryIdentifier =
    organization?.identifier?.find(
      (identifier) => identifier.use === "official"
    )?.value ?? organization?.identifier?.[0]?.value ?? organization?.id ?? "N/A";

  const parentIdentifier =
    organization?.partOf?.identifier?.value ??
    organization?.partOf?.reference?.split("/").pop() ??
    "N/A";

  const organizationKey =
    organization?.identifier?.find((identifier) => identifier.use === "official")
      ?.value ?? organization?.id ?? organization?.name ?? "";

  const parentOrganization = allOrganizations.find((candidate) => {
    const candidateKey =
      candidate.identifier?.find((identifier) => identifier.use === "official")
        ?.value ?? candidate.id ?? candidate.name ?? "";

    return candidateKey === parentIdentifier;
  });

  const childOrganizations = allOrganizations.filter((candidate) => {
    const candidateParent =
      candidate.partOf?.identifier?.value ??
      candidate.partOf?.reference?.split("/").pop() ??
      candidate.partOf?.display ??
      "";

    return candidateParent === organizationKey;
  });

  const managedLocations = allLocations.filter((candidate) =>
    referenceMatchesValue(candidate.managingOrganization, organizationKey)
  );

  const healthcareServices = allHealthcareServices.filter((candidate) =>
    referenceMatchesValue(candidate.providedBy, organizationKey)
  );

  const organizationPath = useMemo(
    () => getOrganizationPath(organization, allOrganizations),
    [allOrganizations, organization]
  );

  //////////////////////////////
  //          Actions         //
  //////////////////////////////

  const goToOrganizationDetails = useCallback(
    (targetOrganizationId?: string | null) => {
      if (targetOrganizationId) {
        navigate(`/Organization/${encodeURIComponent(targetOrganizationId)}`);
      }
    },
    [navigate]
  );

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JunoPage
      titleKey="title.organizationdetails"
      loading={loading}
      fitFooter={fitFooter}
      needsLogin={true}
    >
      <div className="section">
        <Card className="card">
          <Card.Header>
            <Title
              level={2}
              prefix={organization?.resourceType}
              content={organization?.id}
            />
          </Card.Header>
          <Card.Body>
            {organization?.text?.div ? (
              <NarrativeDisplay
                resource={organization}
                onError={onError}
                message={i18n.t("title.error.narrativenotfound")}
              />
            ) : (
              <div className="organization-details-layout">
                {organization?.active !== undefined && (
                  <div className="organization-details-badges">
                    <Badge bg={organization.active ? "success" : "danger"}>
                      {organization.active
                        ? i18n.t("label.active")
                        : i18n.t("label.inactive")}
                    </Badge>
                  </div>
                )}
                <div className="organization-details-grid">
                  <div className="organization-detail-card organization-detail-card-full">
                    <span className="organization-detail-label">
                      {i18n.t("label.organizationpath")}
                    </span>
                    <div className="organization-detail-value organization-path">
                      {organizationPath.length > 0 ? (
                        organizationPath.map((item, index) => {
                          const pathKey =
                            getOrganizationKey(item) || item.id || item.name || `path-${index}`;
                          const isLast = index === organizationPath.length - 1;

                          return (
                            <span className="organization-path-item" key={pathKey}>
                              <Button
                                variant={isLast ? "primary" : "outline-primary"}
                                size="sm"
                                className="rounded-pill"
                                onClick={() =>
                                  goToOrganizationDetails(
                                    item.id ?? getOrganizationKey(item)
                                  )
                                }
                              >
                                {item.name ?? item.id ?? "N/A"}
                              </Button>
                              {!isLast && <span className="organization-path-separator">/</span>}
                            </span>
                          );
                        })
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                  <div className="organization-detail-card">
                    <span className="organization-detail-label">
                      {i18n.t("label.organizationname")}
                    </span>
                    <span className="organization-detail-value">
                      {organization?.name ?? "N/A"}
                    </span>
                  </div>
                  <div className="organization-detail-card">
                    <span className="organization-detail-label">
                      {i18n.t("label.identifier")}
                    </span>
                    <span className="organization-detail-value">
                      {primaryIdentifier}
                    </span>
                  </div>
                  <div className="organization-detail-card">
                    <span className="organization-detail-label">
                      {i18n.t("label.organizationtype")}
                    </span>
                    <span className="organization-detail-value">
                      {organization
                        ? getOrganizationTypeLabel(
                            organization,
                            organizationTypeOptions
                          )
                        : "N/A"}
                    </span>
                  </div>
                  <div className="organization-detail-card">
                    <span className="organization-detail-label">
                      {i18n.t("label.parentidentifier")}
                    </span>
                    <span className="organization-detail-value">
                      {parentOrganization ? (
                        <Button
                          variant="primary"
                          size="sm"
                          className="rounded-pill"
                          onClick={() =>
                            goToOrganizationDetails(parentOrganization.id)
                          }
                        >
                          {parentOrganization.name ?? parentIdentifier}
                        </Button>
                      ) : (
                        parentIdentifier
                      )}
                    </span>
                  </div>
                  <div className="organization-detail-card">
                    <span className="organization-detail-label">
                      {i18n.t("label.aliases")}
                    </span>
                    <span className="organization-detail-value">
                      {organization?.alias?.length
                        ? organization.alias.join(", ")
                        : "N/A"}
                    </span>
                  </div>
                  <div className="organization-detail-card organization-detail-card-full">
                    <span className="organization-detail-label">
                      {i18n.t("label.suborganizations")}
                    </span>
                    <div className="organization-detail-value organization-suborganizations">
                      {childOrganizations.length > 0 ? (
                        childOrganizations.map((child) => (
                          <Button
                            key={child.id ?? child.name}
                            size="sm"
                            variant="outline-primary"
                            className="rounded-pill"
                            onClick={() => goToOrganizationDetails(child.id)}
                          >
                            {child.name ?? child.id ?? "N/A"}
                          </Button>
                        ))
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                  <div className="organization-detail-card organization-detail-card-full">
                    <span className="organization-detail-label">
                      {i18n.t("label.locationsmanaged")}
                    </span>
                    <div className="organization-detail-value organization-suborganizations">
                      {managedLocations.length > 0 ? (
                        managedLocations.map((managedLocation) => (
                          <Button
                            key={managedLocation.id ?? getLocationKey(managedLocation)}
                            size="sm"
                            variant="outline-primary"
                            className="rounded-pill"
                            onClick={() =>
                              navigate(`/Location/${encodeURIComponent(managedLocation.id ?? getLocationKey(managedLocation))}`)
                            }
                          >
                            {getLocationDisplayName(managedLocation)}
                          </Button>
                        ))
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                  <div className="organization-detail-card organization-detail-card-full">
                    <span className="organization-detail-label">
                      {i18n.t("label.healthcareservicesprovided")}
                    </span>
                    <div className="organization-detail-value organization-suborganizations">
                      {healthcareServices.length > 0 ? (
                        healthcareServices.map((service) => (
                          <Button
                            key={service.id ?? service.name}
                            size="sm"
                            variant="outline-primary"
                            className="rounded-pill"
                            onClick={() =>
                              navigate(
                                `/HealthcareService/${encodeURIComponent(
                                  service.id ?? service.name ?? ""
                                )}`
                              )
                            }
                          >
                            {service.name ?? service.id ?? "N/A"}
                          </Button>
                        ))
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </div>
    </JunoPage>
  );
};

export default OrganizationDetails;
